/**
 * Session state, Svelte 5 runes.
 *
 * One module-level object rather than a store-per-value: every screen needs the
 * whole of it, and a single `session.ready` flag is what keeps the app from
 * flashing the sign-in card while Firebase is still restoring a session.
 */
import type { User } from "firebase/auth";
import { onAuth, getClaims, type Claims } from "./auth";

class Session {
  user = $state<User | null>(null);
  claims = $state<Claims | null>(null);
  /** False until Firebase has told us whether anyone is signed in. */
  ready = $state(false);
  error = $state<string | null>(null);

  constructor() {
    onAuth(async (user) => {
      this.user = user;
      this.claims = user ? await getClaims(user) : null;

      // Signed in but carrying no claims is the exact shape of "access was
      // granted after this token was minted" — which is what happens to every
      // new person, since claims can only be attached to a uid that already
      // exists. The cached ID token would otherwise keep saying "no access"
      // for up to an hour and the only cure would be signing out and back in.
      // One forced refresh resolves it; if it still comes back empty, the
      // account genuinely has no access and the gate is correct.
      if (user && !this.claims) {
        this.claims = await getClaims(user, true);
      }

      this.ready = true;
    });
  }

  /** After a claims change, pull a fresh token so the new role is live. */
  async refresh(): Promise<void> {
    if (!this.user) return;
    this.claims = await getClaims(this.user, true);
  }

  get firstName(): string {
    const n = this.user?.displayName ?? this.user?.email ?? "";
    return n.split(/[ @]/)[0] || "there";
  }
}

export const session = new Session();
